import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { formatCurrency } from "../../../lib/format";
import { useExpenseLayout, useExpensesCollapsed } from "../../../lib/settings";
import { Avatar, Button, Card, CardTitle, EmptyState, Icon, Input, Row, TapTarget } from "../../ui";
import { AddExpenseDialog } from "../dialogs/AddExpenseDialog";
import type { Expense, ExpenseFilter, Member, Settlement, SettlementCategory } from "../types";
import { ExpensesTable } from "./ExpensesTable";

interface ExpensesSectionProps {
  settlementId: string;
  householdId: string;
  expenses: Expense[];
  invoices: Settlement["invoices"];
  members: Member[];
  categories: SettlementCategory[];
  currency: string;
  isLocked: boolean;
}

export function ExpensesSection({
  settlementId,
  householdId,
  expenses,
  invoices,
  members,
  categories,
  currency,
  isLocked,
}: ExpensesSectionProps) {
  const [filters, setFilters] = useState<Set<ExpenseFilter>>(() => new Set());
  const [memberFilters, setMemberFilters] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { layout, setLayout } = useExpenseLayout();
  const { collapsed, toggle: toggleCollapsed } = useExpensesCollapsed(settlementId);

  const expensesTotal = useMemo(
    () => expenses.reduce((sum, e) => sum + Number.parseFloat(e.amount), 0),
    [expenses],
  );
  const bodyId = `expenses-body-${settlementId}`;

  const toggleFilter = (f: ExpenseFilter) =>
    setFilters((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });

  const toggleMemberFilter = (userId: string) =>
    setMemberFilters((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("nb-NO");
    const sharedActive = filters.has("shared");
    const privateActive = filters.has("private");
    const manualActive = filters.has("manual");
    const autoActive = filters.has("auto");
    const liveActive = filters.has("live");
    const sourceFilterActive = manualActive || autoActive || liveActive;
    return expenses.filter((e) => {
      // shared/private — hvis bare en av dem er valgt brukes den som filter.
      // Hvis begge eller ingen er valgt finnes ingen restriksjon for denne gruppen.
      if (sharedActive && !privateActive && !e.isShared) return false;
      if (privateActive && !sharedActive && e.isShared) return false;
      // Kilde-gruppen — hvis minst én er valgt, må raden matche minst én av
      // de valgte kildene. Hvis ingen er valgt finnes ingen restriksjon.
      if (sourceFilterActive) {
        const matches =
          (manualActive && e.source === "manual") ||
          (autoActive && e.source === "invoice") ||
          (liveActive && e.source === "live");
        if (!matches) return false;
      }
      // Betalt av — multi-select, viser kun utgifter betalt av minst en av de
      // valgte medlemmene. Tom = ingen restriksjon.
      if (memberFilters.size > 0 && !memberFilters.has(e.paidByUserId)) return false;
      if (query && !e.description.toLocaleLowerCase("nb-NO").includes(query)) return false;
      return true;
    });
  }, [expenses, filters, memberFilters, search]);

  // Filterne er gruppert i to dimensjoner: Type (felles/privat) og Kilde
  // (manuell/fra faktura). Innenfor en gruppe er filtrene gjensidig
  // utelukkende; pa tvers av gruppene er de uavhengige (AND).
  const filterGroups: Array<{
    heading: string;
    options: Array<{ value: ExpenseFilter; label: string; count: number }>;
  }> = [
    {
      heading: t.settlement.expenses.filters.typeHeading,
      options: [
        {
          value: "shared",
          label: t.settlement.expenses.filters.shared,
          count: expenses.filter((e) => e.isShared).length,
        },
        {
          value: "private",
          label: t.settlement.expenses.filters.private,
          count: expenses.filter((e) => !e.isShared).length,
        },
      ],
    },
    {
      heading: t.settlement.expenses.filters.sourceHeading,
      options: [
        {
          value: "manual",
          label: t.settlement.expenses.filters.manual,
          count: expenses.filter((e) => e.source === "manual").length,
        },
        {
          value: "auto",
          label: t.settlement.expenses.filters.fromInvoice,
          count: expenses.filter((e) => e.source === "invoice").length,
        },
        {
          value: "live",
          label: t.settlement.expenses.filters.live,
          count: expenses.filter((e) => e.source === "live").length,
        },
      ],
    },
  ];
  const isFiltered = filters.size > 0 || memberFilters.size > 0 || search.trim().length > 0;
  const totalActiveFilters = filters.size + memberFilters.size;

  return (
    <Card padding="md">
      <Row justify="between" align="center" className={cn(!collapsed && "mb-4")}>
        <TapTarget
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          aria-label={
            collapsed ? t.settlement.expenses.expandLabel : t.settlement.expenses.collapseLabel
          }
          className="group -mx-1 flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left transition-colors hover:bg-ink-50 dark:hover:bg-ink-900/40"
        >
          <Icon
            name="chevron-right"
            size="sm"
            className={cn(
              "shrink-0 text-ink-400 transition-transform duration-150",
              !collapsed && "rotate-90",
            )}
          />
          <Icon name="receipt" size="sm" className="text-ink-500" />
          <div className="flex min-w-0 items-center gap-2">
            <CardTitle className="leading-none">{t.settlement.expenses.heading}</CardTitle>
            {expenses.length > 0 && (
              <span className="truncate text-ink-500 text-xs leading-none">
                {t.settlement.expenses.summary(
                  expenses.length,
                  formatCurrency(expensesTotal, currency),
                )}
              </span>
            )}
          </div>
        </TapTarget>
        {!isLocked && (
          <Button variant="primary" size="sm" iconLeft="plus" onClick={() => setAddOpen(true)}>
            {t.settlement.expenses.addButton}
          </Button>
        )}
      </Row>

      <div id={bodyId} hidden={collapsed}>
        {/* Sok + filter-knapp + layout-toggle. Alle elementer har lik hoyde (h-9).
          Filter-knappen toggler en inline-rad under (ikke dropdown). */}
        <Row gap={2} align="center" className="mb-3">
          <div className="min-w-0 flex-1">
            <Input
              iconLeft="search"
              placeholder={t.settlement.expenses.searchPlaceholder}
              value={search}
              onChange={(ev) => setSearch(ev.target.value)}
              aria-label={t.settlement.expenses.searchLabel}
              className="h-11 sm:h-9"
            />
          </div>
          <Button
            variant="secondary"
            iconLeft="filter"
            aria-label={t.settlement.expenses.filtersButton}
            aria-expanded={filtersOpen}
            aria-controls="expense-filters-row"
            onClick={() => setFiltersOpen((v) => !v)}
            className={cn(
              // WCAG 2.5.5: 44x44px tap-area pa mobil. Kompakt h-9 fra sm+ og opp.
              "h-11 w-11 shrink-0 px-0 sm:h-9 sm:w-auto sm:px-3",
              filtersOpen &&
                "border-ink-400 bg-ink-100 text-ink-900 shadow-none hover:border-ink-400 hover:bg-ink-100 dark:border-ink-400 dark:bg-ink-200",
            )}
          >
            <span className="hidden sm:inline">{t.settlement.expenses.filtersButton}</span>
            {totalActiveFilters > 0 && (
              <span className="ml-1 inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded bg-primary px-1 text-[10px] text-primary-fg">
                {totalActiveFilters}
              </span>
            )}
            <Icon
              name={filtersOpen ? "chevron-up" : "chevron-down"}
              size="xs"
              className="hidden sm:inline-flex"
            />
          </Button>
          <fieldset
            aria-label={t.settlement.expenses.layoutToggle.groupLabel}
            className="inline-flex h-11 shrink-0 rounded-md border border-ink-200 bg-surface p-0.5 shadow-subtle sm:hidden"
          >
            <TapTarget
              aria-pressed={layout === "card"}
              aria-label={t.settlement.expenses.layoutToggle.card}
              onClick={() => setLayout("card")}
              className={cn(
                "inline-flex h-full w-11 items-center justify-center rounded transition-colors",
                layout === "card"
                  ? "bg-ink-100 text-ink-900 dark:bg-ink-200"
                  : "text-ink-500 hover:text-ink-900",
              )}
            >
              <Icon name="layout-grid" size="xs" />
            </TapTarget>
            <TapTarget
              aria-pressed={layout === "table"}
              aria-label={t.settlement.expenses.layoutToggle.table}
              onClick={() => setLayout("table")}
              className={cn(
                "inline-flex h-full w-11 items-center justify-center rounded transition-colors",
                layout === "table"
                  ? "bg-ink-100 text-ink-900 dark:bg-ink-200"
                  : "text-ink-500 hover:text-ink-900",
              )}
            >
              <Icon name="rows-3" size="xs" />
            </TapTarget>
          </fieldset>
        </Row>

        {/* Inline filter-rad — vises under sok-raden nar filter er apnet. Chips
          flyter horisontalt og wrapper til ny linje nar de ikke far plass. */}
        {filtersOpen && (
          <div id="expense-filters-row" className="mb-3 flex flex-wrap items-center gap-1.5">
            {filterGroups.flatMap((group) =>
              group.options.map((opt) => {
                const active = filters.has(opt.value);
                return (
                  <TapTarget
                    key={opt.value}
                    aria-pressed={active}
                    onClick={() => toggleFilter(opt.value)}
                    className={cn(
                      // WCAG-friendly tap-area pa mobil; kompakt pa sm+.
                      "inline-flex h-10 items-center gap-1.5 rounded-md px-3 font-medium text-xs transition-colors sm:h-7 sm:px-2.5",
                      active
                        ? "bg-primary/10 text-primary hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/25"
                        : "bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-ink-200 dark:hover:bg-ink-300",
                    )}
                  >
                    {opt.label}
                    <span
                      className={cn(
                        "inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded px-1 text-[10px]",
                        active ? "bg-primary text-primary-fg" : "bg-ink-200 text-ink-600",
                      )}
                    >
                      {opt.count}
                    </span>
                  </TapTarget>
                );
              }),
            )}
            {/* Betalt av — chip per medlem med avatar. Multi-select. */}
            {members.map((m) => {
              const active = memberFilters.has(m.userId);
              const count = expenses.filter((e) => e.paidByUserId === m.userId).length;
              const displayName = m.displayName ?? m.email.split("@")[0];
              return (
                <TapTarget
                  key={m.userId}
                  aria-pressed={active}
                  onClick={() => toggleMemberFilter(m.userId)}
                  className={cn(
                    "inline-flex h-10 items-center gap-1.5 rounded-md pr-3 pl-1 font-medium text-xs transition-colors sm:h-7 sm:pr-2.5",
                    active
                      ? "bg-primary/10 text-primary hover:bg-primary/15 dark:bg-primary/20 dark:hover:bg-primary/25"
                      : "bg-ink-100 text-ink-700 hover:bg-ink-200 dark:bg-ink-200 dark:hover:bg-ink-300",
                  )}
                >
                  <Avatar name={m.displayName ?? m.email} size="sm" />
                  <span className="max-w-[8rem] truncate">{displayName}</span>
                  <span
                    className={cn(
                      "inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded px-1 text-[10px]",
                      active ? "bg-primary text-primary-fg" : "bg-ink-200 text-ink-600",
                    )}
                  >
                    {count}
                  </span>
                </TapTarget>
              );
            })}
            {totalActiveFilters > 0 && (
              <TapTarget
                onClick={() => {
                  setFilters(new Set());
                  setMemberFilters(new Set());
                }}
                className="ml-1 text-accent-700 text-xs hover:text-accent-900 dark:text-accent-300 dark:hover:text-accent-200"
              >
                {t.settlement.expenses.filtersClear}
              </TapTarget>
            )}
          </div>
        )}

        {filtered.length === 0 ? (
          <EmptyState
            icon="receipt"
            title={t.settlement.expenses.emptyTitle}
            description={
              isFiltered
                ? t.settlement.expenses.emptyDescriptionFiltered
                : t.settlement.expenses.emptyDescriptionAll
            }
          />
        ) : (
          <ExpensesTable
            settlementId={settlementId}
            householdId={householdId}
            allExpenses={expenses}
            expenses={filtered}
            invoices={invoices}
            members={members}
            categories={categories}
            currency={currency}
            isLocked={isLocked}
            layout={layout}
            search={search}
          />
        )}
      </div>

      <AddExpenseDialog
        settlementId={settlementId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        members={members}
        categories={categories}
      />
    </Card>
  );
}
